import { Injectable } from '@angular/core';
import { Customer } from '@app/@shared/models/customer.model';
import { User } from '@app/@shared/models/user.model';
import { Timestamp } from 'firebase/firestore';

export interface Credentials {
  // Customize received credentials here
  id: string;
  name: string;
  token: string;
  email: string;
  phone: string;
  emailVerified: boolean;
  phoneVerified: boolean;
  idVerified: boolean;
  idChecking: boolean;
  user: User;
  customer: Customer;
}

const credentialsKey = 'credentials';

/**
 * Provides storage for authentication credentials.
 * The Credentials interface should be replaced with proper implementation.
 */
@Injectable({
  providedIn: 'root',
})
export class CredentialsService {
  private _credentials: Credentials | null = null;

  constructor() {
    const reviver = (key: string, value: any): any => {
      if (key === 'user') {
        if (value['createdAt']) {
          value['createdAt'] = new Timestamp(value['createdAt'].seconds, value['createdAt'].nanoseconds);
        }
        if (value['modifiedAt']) {
          value['modifiedAt'] = new Timestamp(value['modifiedAt'].seconds, value['modifiedAt'].nanoseconds);
        }
        if (value['lastLogin']) {
          value['lastLogin'] = new Timestamp(value['lastLogin'].seconds, value['lastLogin'].nanoseconds);
        }
        if (value['actualLogin']) {
          value['actualLogin'] = new Timestamp(value['actualLogin'].seconds, value['actualLogin'].nanoseconds);
        }
      }

      if (key === 'customer') {
        if (value['createdAt']) {
          value['createdAt'] = new Timestamp(value['createdAt'].seconds, value['createdAt'].nanoseconds);
        }
        if (value['modifiedAt']) {
          value['modifiedAt'] = new Timestamp(value['modifiedAt'].seconds, value['modifiedAt'].nanoseconds);
        }
        if (value['dateOfBirth']) {
          value['dateOfBirth'] = new Timestamp(value['dateOfBirth'].seconds, value['dateOfBirth'].nanoseconds);
        }
      }

      return value;
    };
    const savedCredentials = sessionStorage.getItem(credentialsKey) || localStorage.getItem(credentialsKey);
    if (savedCredentials) {
      this._credentials = JSON.parse(savedCredentials, reviver);
    }
  }

  /**
   * Checks is the user is authenticated.
   * @return True if the user is authenticated.
   */
  isAuthenticated(): boolean {
    return !!this.credentials;
  }

  /**
   * Gets the user credentials.
   * @return The user credentials or null if the user is not authenticated.
   */
  get credentials(): Credentials | null {
    return this._credentials;
  }

  /**
   * Sets the user credentials.
   * The credentials may be persisted across sessions by setting the `remember` parameter to true.
   * Otherwise, the credentials are only persisted for the current session.
   * @param credentials The user credentials.
   * @param remember True to remember credentials across sessions.
   */
  setCredentials(credentials?: Credentials, remember?: boolean) {
    this._credentials = credentials || null;

    if (credentials) {
      const storage = remember ? localStorage : sessionStorage;
      storage.setItem(credentialsKey, JSON.stringify(credentials));
    } else {
      sessionStorage.removeItem(credentialsKey);
      localStorage.removeItem(credentialsKey);
    }
  }
}
